/**
 * Tenant Context Provider
 * Manages tenant context throughout the application lifecycle
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TenantInfo, TenantBranding, FeatureFlag } from '../types';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface TenantContextValue {
  tenant: TenantInfo | null;
  isLoading: boolean;
  error: string | null;
  isFeatureEnabled: (feature: FeatureFlag | string) => boolean;
  isModuleEnabled: (module: string) => boolean;
  refreshTenant: () => Promise<void>;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultTenantContext: TenantContextValue = {
  tenant: null,
  isLoading: true,
  error: null,
  isFeatureEnabled: () => false,
  isModuleEnabled: () => false,
  refreshTenant: async () => {},
};

// ============================================================================
// CONTEXT
// ============================================================================

export const TenantContext = createContext<TenantContextValue>(defaultTenantContext);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface TenantProviderProps {
  children: ReactNode;
  tenantSlug?: string;
  apiBaseUrl?: string;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function TenantProvider({
  children,
  tenantSlug: initialSlug,
  apiBaseUrl = '/api/platform/v1'
}: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Resolves tenant slug from various sources
   */
  const resolveTenantSlug = (): string | null => {
    // Priority 1: Explicit prop
    if (initialSlug) return initialSlug;

    // Priority 2: Subdomain (e.g., acme.platform.com)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // Check for subdomain pattern
      if (parts.length >= 3) {
        const subdomain = parts[0];
        // Exclude common non-tenant subdomains
        if (!['www', 'app', 'api', 'admin', 'localhost'].includes(subdomain)) {
          return subdomain;
        }
      }

      // Priority 3: Query parameter (for development)
      const urlParams = new URLSearchParams(window.location.search);
      const tenantParam = urlParams.get('tenant');
      if (tenantParam) return tenantParam;

      // Priority 4: Local storage (for session persistence)
      const storedTenant = localStorage.getItem('platform_tenant_slug');
      if (storedTenant) return storedTenant;
    }

    return null;
  };

  /**
   * Fetches tenant information from the API
   */
  const fetchTenant = async (slug: string): Promise<TenantInfo> => {
    const response = await fetch(`${apiBaseUrl}/tenants/${slug}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': slug,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Tenant not found');
      }
      throw new Error('Failed to fetch tenant information');
    }

    const data = await response.json();
    return data.data as TenantInfo;
  };

  /**
   * Loads tenant information
   */
  const loadTenant = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const slug = resolveTenantSlug();

      if (!slug) {
        // No tenant context - might be platform admin or public page
        setTenant(null);
        setIsLoading(false);
        return;
      }

      const tenantData = await fetchTenant(slug);
      setTenant(tenantData);

      // Store for session persistence
      if (typeof window !== 'undefined') {
        localStorage.setItem('platform_tenant_slug', slug);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refreshes tenant information
   */
  const refreshTenant = async () => {
    await loadTenant();
  };

  /**
   * Checks if a feature is enabled for the current tenant
   */
  const isFeatureEnabled = (feature: FeatureFlag | string): boolean => {
    if (!tenant) return false;
    return tenant.features.includes(feature);
  };

  /**
   * Checks if a module is enabled for the current tenant
   */
  const isModuleEnabled = (module: string): boolean => {
    if (!tenant) return false;
    return tenant.modules.includes(module);
  };

  // Load tenant on mount
  useEffect(() => {
    loadTenant();
  }, [initialSlug]);

  const contextValue: TenantContextValue = {
    tenant,
    isLoading,
    error,
    isFeatureEnabled,
    isModuleEnabled,
    refreshTenant,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access tenant context
 * @throws Error if used outside TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook to require tenant context (throws if no tenant)
 * @throws Error if no tenant is available
 */
export function useRequiredTenant(): TenantInfo {
  const { tenant, isLoading, error } = useTenant();

  if (isLoading) {
    throw new Error('Tenant is still loading');
  }

  if (error) {
    throw new Error(`Tenant error: ${error}`);
  }

  if (!tenant) {
    throw new Error('No tenant context available');
  }

  return tenant;
}

/**
 * Hook to check if a feature is enabled
 */
export function useFeature(feature: FeatureFlag | string): boolean {
  const { isFeatureEnabled } = useTenant();
  return isFeatureEnabled(feature);
}

/**
 * Hook to check if a module is enabled
 */
export function useModule(module: string): boolean {
  const { isModuleEnabled } = useTenant();
  return isModuleEnabled(module);
}

/**
 * Hook to access tenant branding
 */
export function useBranding(): TenantBranding | null {
  const { tenant } = useTenant();
  return tenant?.branding ?? null;
}
